import { generatePath } from 'react-router-dom'
import dayjs from 'dayjs'

import { Spinner } from 'common/Spinner/Spinner'
import { Text } from 'blaze-ui/Text/Text'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { Check } from 'blaze-ui/icons/16'
import { AlertTriangle } from 'blaze-ui/icons'

import { DESIGN_PATH, DOC_PATH } from 'paths'
import { AccountAvatar } from '../AccountToggle/AccountAvatar'
import { ProviderIcon } from 'components/PostPreviewModal/ProviderIcon'

import Styles from './AccountPostStatus.module.scss'
import { PostState } from 'components/PostPreviewModalV2/types'
import { Tooltip } from 'components/Tooltip/Tooltip'

interface Props {
  state: PostState
  isDesign?: boolean
  isThisFile?: boolean
}

export const AccountPostStatus = ({ state, isDesign, isThisFile }: Props) => {
  const isPosting: boolean = state.runtime === 'creating' || state.runtime === 'publishing'
  const isSuccess: boolean = state.runtime === 'success'
  const isFailure: boolean = state.runtime === 'failure'

  const renderLabel = () =>
    state.account?.name ?? state.file?.title ?? dayjs(state.postAt ?? undefined).format('[File] YYYY-MM-DD')

  const renderAvatarOrIcon = () =>
    state.account ? <AccountAvatar account={state.account} /> : <ProviderIcon provider={state.provider} size={24} />

  const renderSuccessInner = () => {
    if (state.postUrl) {
      return (
        <ButtonLink size="xs" to={state.postUrl} target="_blank" variant="tertiary">
          Open&nbsp;Post
        </ButtonLink>
      )
    }

    if (state.isOriginalFile || isThisFile) return <span className={Styles.thisFile}>This&nbsp;File</span>

    if (state.fileId) {
      const to = isDesign
        ? generatePath(DESIGN_PATH, { key: state.fileId })
        : generatePath(DOC_PATH, { slug: state.fileId })

      return (
        <ButtonLink size="xs" to={to} target="_blank" variant="tertiary">
          Open&nbsp;File
        </ButtonLink>
      )
    }

    return <Check color="var(--green-70)" />
  }

  const renderStatus = () => {
    if (isPosting) {
      return (
        <div className={Styles.statusIndicator}>
          <Spinner />
        </div>
      )
    }

    if (isSuccess) {
      return <div className={Styles.statusIndicator}>{renderSuccessInner()}</div>
    }

    if (isFailure) {
      const to = isDesign
        ? generatePath(DESIGN_PATH, { key: state.fileId })
        : generatePath(DOC_PATH, { slug: state.fileId })
      return (
        <div className={Styles.statusIndicator}>
          <Tooltip label={state.error}>
            <div>
              <AlertTriangle color="var(--red-70)" />
            </div>
          </Tooltip>
          <ButtonLink size="xs" to={to} target="_blank" variant="tertiary">
            Open&nbsp;File
          </ButtonLink>
        </div>
      )
    }

    return null
  }

  return (
    <div className={Styles.root}>
      {renderAvatarOrIcon()}

      <div className={Styles.nameAndToggle}>
        <Text>{renderLabel()}</Text>
        {renderStatus()}
      </div>
    </div>
  )
}
